import { useEffect, useState } from "react";
import { useForm, useStore } from "@tanstack/react-form";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/Button";
import { Form } from "../../../components/Form";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { FormActions } from "../../../components/FormActions";
import { InfoBox } from "../../../components/InfoBox";
import { Input } from "../../../components/Input";
import { IntegrationType } from "@KassaroAB/shared/types/enums/IntegrationType";
import type { RestIntegration } from "@KassaroAB/shared/types/integrations/integration";
import type { ChainId } from "@KassaroAB/shared/types/ids";
import {
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
} from "../../../query/integrationsQuery";
import { useGetShopifyOauthUrlMutation } from "../../../query/shopifyIntegrationQuery";
import { ShopifyPartnerGuideModal } from "./ShopifyPartnerGuideModal";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";

const ErrorMessage = styled.div`
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-top: ${spacing.x05}px;
`;

type RestIntegrationShopify = Extract<
  RestIntegration,
  { type: IntegrationType.SHOPIFY }
>;

interface ShopifyFormProps {
  chainId: ChainId;
  integration?: RestIntegrationShopify;
  onCancel: () => void;
}

const SectionTitle = styled.h3`
  margin: 0 0 ${spacing.x2}px;
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

export const ShopifyForm = ({
  chainId,
  integration,
  onCancel,
}: ShopifyFormProps) => {
  const { t } = useTranslation();
  const createMutation = useCreateIntegrationMutation(chainId);
  const updateMutation = useUpdateIntegrationMutation(chainId);
  const oauthMutation = useGetShopifyOauthUrlMutation();

  const [provisionMessage, setProvisionMessage] = useState<string | null>(null);
  const [oauthErrorMessage, setOauthErrorMessage] = useState<string | null>(
    null,
  );
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const form = useForm({
    defaultValues: {
      shopDomain: integration?.shopDomain ?? "",
      shopifyClientId: integration?.shopifyClientId ?? "",
      shopifyClientSecret: "",
    },
    onSubmit: ({ value }) => {
      setProvisionMessage(null);

      const normalizedShopDomain = value.shopDomain.trim();
      const integrationPayload = {
        type: IntegrationType.SHOPIFY as const,
        name: normalizedShopDomain,
        shopDomain: normalizedShopDomain,
        shopifyClientId: value.shopifyClientId,
        shopifyClientSecret: value.shopifyClientSecret,
        requiresReauth: false,
      };

      if (integration) {
        updateMutation.mutate(
          {
            integrationId: integration.id,
            integration: {
              type: IntegrationType.SHOPIFY,
              name: normalizedShopDomain,
              shopDomain: normalizedShopDomain,
              shopifyClientId: value.shopifyClientId,
              ...(value.shopifyClientSecret
                ? { shopifyClientSecret: value.shopifyClientSecret }
                : {}),
              requiresReauth: false,
            },
          },
          {
            onSuccess: () => {
              setProvisionMessage(t("shopifyIntegrationForm.provisionSaved"));
              form.setFieldValue("shopifyClientSecret", "");
            },
          },
        );
        return;
      }

      createMutation.mutate(integrationPayload, {
        onSuccess: () => {
          setProvisionMessage(t("shopifyIntegrationForm.provisionSaved"));
          form.setFieldValue("shopifyClientSecret", "");
        },
      });
    },
  });

  const shopDomain = useStore(form.store, (state) => state.values.shopDomain);

  useEffect(() => {
    if (!integration) {
      return;
    }
    form.setFieldValue("shopDomain", integration.shopDomain);
    form.setFieldValue("shopifyClientId", integration.shopifyClientId ?? "");
  }, [integration, form]);

  const handleConnect = () => {
    const normalizedShopDomain = shopDomain.trim();
    if (!normalizedShopDomain) {
      return;
    }
    setOauthErrorMessage(null);
    oauthMutation
      .mutateAsync({
        chainId,
        shopDomain: normalizedShopDomain,
        integrationId: integration?.id,
      })
      .then((response) => {
        window.location.href = response.url;
      })
      .catch((error: Error) => {
        if (error.message.includes("Contact Kassaro support")) {
          setOauthErrorMessage(t("shopifyIntegrationForm.notProvisioned"));
          return;
        }
        setOauthErrorMessage(
          error.message || t("shopifyIntegrationForm.oauthError"),
        );
      });
  };

  const isSavingCredentials =
    createMutation.isPending || updateMutation.isPending;
  const saveCredentialsError =
    createMutation.error?.message ?? updateMutation.error?.message;

  return (
    <>
      <FormGroup>
        <InfoBox variant="info">{t("shopifyIntegrationForm.summary")}</InfoBox>
        <Button type="button" $color="secondary" onClick={() => setIsGuideOpen(true)}>
          {t("shopifyIntegrationForm.guideLink")}
        </Button>
      </FormGroup>

      <ShopifyPartnerGuideModal
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
      />

      {integration ? (
        <FormGroup>
          <FormLabel>{t("shopifyIntegrationForm.statusTitle")}</FormLabel>
          <InfoBox variant="info">
            {t("shopifyIntegrationForm.statusLine", {
              provisioned: integration.shopifyClientId
                ? t("common.yes")
                : t("common.no"),
              connected: integration.scopes?.length
                ? t("common.yes")
                : t("common.no"),
            })}
          </InfoBox>
        </FormGroup>
      ) : null}

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <SectionTitle>{t("shopifyIntegrationForm.provisionTitle")}</SectionTitle>

        {provisionMessage ? (
          <FormGroup>
            <InfoBox variant="success">{provisionMessage}</InfoBox>
          </FormGroup>
        ) : null}

        {saveCredentialsError ? (
          <FormGroup>
            <InfoBox variant="error">{saveCredentialsError}</InfoBox>
          </FormGroup>
        ) : null}

        <form.Field
          name="shopDomain"
          validators={{
            onChange: ({ value }) =>
              !value.trim() ? t("shopifyProvision.shopDomainRequired") : undefined,
          }}
        >
          {(field) => (
            <FormGroup>
              <FormLabel htmlFor="shopifyShopDomain">
                {t("shopifyProvision.shopDomain")}
              </FormLabel>
              <Input
                id="shopifyShopDomain"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                placeholder={t("shopifyProvision.shopDomainPlaceholder")}
                required
              />
              {field.state.meta.errors.length > 0 && (
                <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
              )}
            </FormGroup>
          )}
        </form.Field>

        <form.Field
          name="shopifyClientId"
          validators={{
            onChange: ({ value }) =>
              !value ? t("shopifyProvision.clientIdRequired") : undefined,
          }}
        >
          {(field) => (
            <FormGroup>
              <FormLabel htmlFor="shopifyClientId">
                {t("shopifyProvision.clientId")}
              </FormLabel>
              <Input
                id="shopifyClientId"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required
              />
              {field.state.meta.errors.length > 0 && (
                <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
              )}
            </FormGroup>
          )}
        </form.Field>

        <form.Field
          name="shopifyClientSecret"
          validators={{
            onChange: ({ value }) => {
              if (integration?.shopifyClientId) {
                return undefined;
              }
              return !value
                ? t("shopifyProvision.clientSecretRequired")
                : undefined;
            },
          }}
        >
          {(field) => (
            <FormGroup>
              <FormLabel htmlFor="shopifyClientSecret">
                {t("shopifyProvision.clientSecret")}
              </FormLabel>
              <Input
                id="shopifyClientSecret"
                type="password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                required={!integration?.shopifyClientId}
              />
              {field.state.meta.errors.length > 0 && (
                <ErrorMessage>{field.state.meta.errors[0]}</ErrorMessage>
              )}
            </FormGroup>
          )}
        </form.Field>

        <FormActions>
          <Button type="submit" disabled={isSavingCredentials}>
            {t("shopifyIntegrationForm.saveCredentials")}
          </Button>
        </FormActions>
      </Form>

      <FormGroup>
        <SectionTitle>{t("shopifyIntegrationForm.connectTitle")}</SectionTitle>
        {oauthErrorMessage ? (
          <InfoBox variant="error">{oauthErrorMessage}</InfoBox>
        ) : null}
        {integration?.shopifyClientId && !integration.scopes?.length ? (
          <InfoBox variant="info">
            {t("shopifyIntegrationForm.readyToConnect")}
          </InfoBox>
        ) : null}
      </FormGroup>

      <FormActions>
        <Button type="button" $color="secondary" onClick={onCancel}>
          {t("common.close")}
        </Button>
        <Button
          type="button"
          onClick={handleConnect}
          disabled={oauthMutation.isPending || !shopDomain.trim()}
        >
          {integration?.scopes?.length
            ? t("shopifyIntegrationForm.reconnect")
            : t("shopifyIntegrationForm.connect")}
        </Button>
      </FormActions>
    </>
  );
};
